import React, { useState, useEffect } from 'react'
import axios from 'axios';
import {baseUrl, limit} from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import Select from 'react-select';
import Loader from '../../../components/Loader';
import { menuAboveTable } from "../../../comman/selectMenu";

function List() {

    const [categoryList, setCategoryList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    // Status defaults to Active, matching the other master lists. '' is All.
    const [statusFilter, setStatusFilter] = useState({ value: '1', label: 'Active' });
    const [loading, setLoading] = useState(false);

    // role 0 = superadmin, 1 = admin. Both may enable and disable; only the superadmin may delete.
    const role = String(localStorage.getItem('userRole'));
    // Which buttons this user gets is Role Master's call now, not a role number's -- see
    // comman/permissions.js. `role` is still read because it is what the delete and enable
    // endpoints are told to check against.
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');

    const navigate = useNavigate();

    useEffect(() => {
        getCategoryList();
    }, []);

    // categoryList is a dependency, not just the two filters: the fetch does not seed filteredList,
    // so the default Active filter holds from the first render rather than after the first keystroke.
    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        const status = statusFilter?.value ?? '';
        setFilteredList(categoryList.filter((item) => {
            const matchesQuery = !query || (item.name ?? '').toLowerCase().includes(query);
            const matchesStatus = status === '' || String(item.status) === status;
            return matchesQuery && matchesStatus;
        }));
    }, [searchQuery, statusFilter, categoryList]);

    const getCategoryList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/vessel_category/list?page=${page}&limit=${limit}`);
            setCategoryList(response.data.data ?? []);
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not load the vessel categories.');
        }
        setLoading(false);
    }

    const handelFilterChange = (e) => {
        const { value } = e.target;
        setSearchQuery(value);
    };

    const handleClear = () => {
        setSearchQuery('');
        setStatusFilter({ value: '1', label: 'Active' });
    };

    const disableCategory = async (id) => {
        if (!window.confirm("Are you sure you want to disable this vessel category?")) return;
        try {
            await axios.delete(`${baseUrl}/vessel_category/delete/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not disable the vessel category.');
        }
    };

    const enableCategory = async (id) => {
        if (!window.confirm("Enable this vessel category?")) return;
        try {
            await axios.post(`${baseUrl}/vessel_category/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the vessel category.');
        }
    };

    // Delete removes the row, unlike Disable which is recoverable, so its confirmation says so.
    // Whether anything still references it is the backend's call -- it counts and refuses with the
    // reason, which is what gets shown here.
    const destroyCategory = async (id) => {
        if (!window.confirm("Permanently DELETE this vessel category? Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/vessel_category/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the vessel category.');
        }
    };

    const editCategory = async (id) => {
        navigate(`/vessel_category/edit/${btoa(id)}`, { state: categoryList.filter(item => item.id == id) });
    }

    return (
        <div className="p-4">
            {loading && <Loader/>}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Vessel Category Master</h1>
                <div>
                    <Link to="/vessel_category/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Vessel Category</button>
                    </Link>
                </div>
            </div>
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <div className="pb-4 flex flex-column sm:flex-row flex-wrap space-y-4 sm:space-y-0 items-center justify-between">
                    <label htmlFor="table-search" className="sr-only">Search</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 rtl:inset-r-0 rtl:right-0 flex items-center ps-3 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path></svg>
                        </div>
                        <input type="text" value={searchQuery} onChange={handelFilterChange} id="table-search" className="block p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Search for items" />
                    </div>
                    <div className="flex items-center gap-4 ms-4 me-auto">
                        <Select
                            {...menuAboveTable}
                            classNamePrefix="select"
                            className="basic-multi-select w-36"
                            value={statusFilter}
                            name="status"
                            onChange={(selected) => setStatusFilter(selected)}
                            options={[
                                { value: '', label: 'All' },
                                { value: '1', label: 'Active' },
                                { value: '0', label: 'Inactive' }
                            ]}
                            placeholder="Status"
                            isClearable={false}
                        />
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                        >
                            Clear
                        </button>
                    </div>
                </div>
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="px-6 py-3">Vessel Category</th>
                            <th scope="col" className="px-6 py-3">Status</th>
                            <th scope="col" className="px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                    {filteredList.map((item) => (
                        <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                            <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.name}</th>
                            <td className="px-6 py-4">{Number(item.status) === 1 ? 'Active' : 'Inactive'}</td>
                            <td className="px-6 py-4">
                            <div className="flex gap-2">
                                {item.status == 1 && <button
                                    type="button"
                                    onClick={() => editCategory(item.id)}
                                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                    Edit
                                </button>}
                                {item.status == 1 && canDisable && <button
                                    type="button"
                                    onClick={() => disableCategory(item.id)}
                                    className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                    Disable
                                </button>}
                                {item.status == 0 && canActivate && <button
                                    type="button"
                                    onClick={() => enableCategory(item.id)}
                                    className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                    Enable
                                </button>}
                                {canDelete && <button
                                    type="button"
                                    onClick={() => destroyCategory(item.id)}
                                    className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                    Delete
                                </button>}
                            </div>
                            </td>
                        </tr>))}

                    </tbody>
                </table>
            </div>
        </div>
  );
}

export default List;
